import { Controller } from '@hotwired/stimulus';
import MentionInput from '../utils/mention_input';
import * as bootstrap from 'bootstrap';

export default class extends Controller {
  static targets = ['currentMessage', 'form', 'mentionDropdown', 'hiddenInput', 'voiceButton', 'voiceCancel', 'voiceConfirm'];

  static values = {
    mentionsUrl: String,
    transcribeUrl: String,
    mentionableTypes: Array,
    draftKey: String
  };

  connect() {
    this.initModalScroll();
    this.initMentions();
    this.initOptionButtons();
    this.initDraft();
    this.initStopTooltip();
  }

  disconnect() {
    this.mentionInput?.destroy();
    document.removeEventListener('click', this.onOptionClick);
    clearTimeout(this.draftTimer);
    document.removeEventListener('click', this.onStopClick);
    this.stopStream();
  }

  // --- Initialization ---

  initModalScroll() {
    const modal = document.getElementById('basic-question-modal');
    if (!modal || modal.classList.contains('show')) {
      requestAnimationFrame(() => this.scrollToBottom());
    } else {
      modal.addEventListener('shown.bs.modal', () => this.scrollToBottom(), { once: true });
    }
  }

  initMentions() {
    if (!this.hasCurrentMessageTarget || !this.hasMentionDropdownTarget || !this.hasMentionsUrlValue) {
      return;
    }

    this.mentionInput = new MentionInput(
      this.currentMessageTarget,
      this.mentionDropdownTarget,
      {
        fetchResults: (query) => this.fetchMentions(query),
        renderItem: (item) => `
          <i class="fa fa-${item.icon || 'user'} me-2"></i>
          ${this.escapeHtml(item.label)}
          <small class="text-muted ms-2">${item.type}</small>
        `
      }
    );

    this.currentMessageTarget.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.formTarget?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  initOptionButtons() {
    // Buttons render outside this controller's element.
    this.onOptionClick = (event) => {
      const button = event.target.closest('.chat-option-btn');
      if (!button || button.disabled) return;

      event.preventDefault();
      button.closest('.chat-options')?.querySelectorAll('.chat-option-btn').forEach((b) => { b.disabled = true; });
      button.classList.add('active');
      this.setMessageAndSubmit(button.innerText.trim());
    };
    document.addEventListener('click', this.onOptionClick);
  }

  initStopTooltip() {
    // The stop button arrives by turbo stream, so delegate instead of initializing on load.
    bootstrap.Tooltip.getOrCreateInstance(document.body, { selector: '#chat-stop-btn' });
    this.onStopClick = (event) => {
      const button = event.target.closest('#chat-stop-btn');
      if (button) bootstrap.Tooltip.getInstance(button)?.dispose();
    };
    document.addEventListener('click', this.onStopClick);
  }

  initDraft() {
    if (!this.hasCurrentMessageTarget || !this.hasDraftKeyValue) return;

    this.restoreDraft();
    this.onDraftInput = () => {
      this.clearBlankContent();
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => this.saveDraft(), 300);
    };
    this.currentMessageTarget.addEventListener('input', this.onDraftInput);
    this.formTarget?.addEventListener('submit', () => this.clearDraft());
  }

  restoreDraft() {
    const draft = this.readDraft();
    if (!draft || this.isBlankHtml(draft)) return;

    if (this.currentMessageTarget.isContentEditable) {
      this.currentMessageTarget.innerHTML = draft;
    } else {
      this.currentMessageTarget.value = draft;
    }
  }

  readDraft() {
    try {
      return localStorage.getItem(this.draftKeyValue);
    } catch {
      return null;
    }
  }

  saveDraft() {
    const target = this.currentMessageTarget;
    const draft = target.isContentEditable ? target.innerHTML : target.value;
    try {
      if (!this.isBlankHtml(draft)) {
        localStorage.setItem(this.draftKeyValue, draft);
      } else {
        localStorage.removeItem(this.draftKeyValue);
      }
    } catch {
      return null;
    }
  }

  clearBlankContent() {
    const target = this.currentMessageTarget;
    if (target.isContentEditable && this.isBlankHtml(target.innerHTML)) target.innerHTML = '';
  }

  isBlankHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return !div.textContent.trim() && !div.querySelector('.mention');
  }

  clearDraft() {
    clearTimeout(this.draftTimer);
    try {
      localStorage.removeItem(this.draftKeyValue);
    } catch {
      return null;
    }
  }

  // --- Actions ---

  handleKeydown(event) {
    if (event.key !== 'Enter') return;

    if (event.shiftKey) return;

    if (!this.mentionDropdownTarget.classList.contains('d-none')) return;

    event.preventDefault();
    this.formTarget.requestSubmit();
  }

  setMessageAndSubmit(text) {
    if (this.currentMessageTarget.isContentEditable) {
      this.currentMessageTarget.innerText = text;
    } else {
      this.currentMessageTarget.value = text;
    }

    this.formTarget.requestSubmit();
  }

  handleSubmit() {
    if (!this.mentionInput || !this.hasHiddenInputTarget) return;

    this.hiddenInputTarget.value = this.mentionInput.getTokenizedText();
    this.currentMessageTarget.innerHTML = '';
  }

  async toggleRecording() {
    if (this.recorder?.state === 'recording') {
      this.confirmRecording();
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      alert('Microphone access is needed to speak your question.');
      return;
    }

    const chunks = [];
    this.recorder = new MediaRecorder(this.stream, this.recordingOptions());
    this.recorder.addEventListener('dataavailable', (event) => chunks.push(event.data));
    this.recorder.addEventListener('stop', () => {
      this.stopStream();
      if (this.recordingCancelled) {
        this.setVoiceState('idle');
      } else {
        this.transcribe(new Blob(chunks, { type: this.recorder.mimeType }));
      }
    });
    this.recordingCancelled = false;
    this.recorder.start();
    this.setVoiceState('recording');
    this.startVoiceBars();
  }

  confirmRecording() {
    this.recorder?.stop();
  }

  cancelRecording() {
    this.recordingCancelled = true;
    this.recorder?.stop();
  }

  // Bounces the bars in the mic button with the speaker's volume across a few frequency bands.
  startVoiceBars() {
    const bars = this.voiceButtonTarget.querySelectorAll('.voice-bar');
    this.audioContext = new AudioContext();
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 64;
    this.audioContext.createMediaStreamSource(this.stream).connect(analyser);

    const levels = new Uint8Array(analyser.frequencyBinCount);
    const bandSize = Math.floor(levels.length / bars.length / 2);
    const draw = () => {
      analyser.getByteFrequencyData(levels);
      bars.forEach((bar, index) => {
        const band = levels.slice(index * bandSize, (index + 1) * bandSize);
        const level = band.reduce((sum, value) => sum + value, 0) / band.length / 255;
        bar.style.transform = `scaleY(${Math.max(0.15, Math.min(1, level * 1.6))})`;
      });
      this.voiceBarsFrame = requestAnimationFrame(draw);
    };
    draw();
  }

  recordingOptions() {
    // Chirp cannot decode opus inside mp4, which Chrome offers, so prefer webm and leave mp4 (AAC) for Safari.
    const types = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
    const mimeType = types.find((type) => MediaRecorder.isTypeSupported(type));
    return mimeType ? { mimeType } : {};
  }

  async transcribe(blob) {
    this.setVoiceState('transcribing');

    const body = new FormData();
    body.append('audio', blob, 'speech');

    try {
      const response = await fetch(this.transcribeUrlValue, {
        method: 'POST',
        body,
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
      });
      if (!response.ok) throw new Error(response.statusText);

      const { text } = await response.json();
      this.appendMessage(text);
    } catch (e) {
      console.error('Error transcribing speech:', e);
      alert('Sorry, your question could not be transcribed. Please try again or type it instead.');
    } finally {
      this.setVoiceState('idle');
    }
  }

  appendMessage(text) {
    if (!text) return;

    const target = this.currentMessageTarget;
    if (target.isContentEditable) {
      target.innerText = [target.innerText.trim(), text].filter(Boolean).join(' ');
    } else {
      target.value = [target.value.trim(), text].filter(Boolean).join(' ');
    }
    target.dispatchEvent(new Event('input', { bubbles: true }));
    this.moveCursorToEnd(target);
  }

  moveCursorToEnd(target) {
    target.focus();
    if (target.isContentEditable) {
      const range = document.createRange();
      range.selectNodeContents(target);
      range.collapse(false);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    } else {
      target.setSelectionRange(target.value.length, target.value.length);
    }
  }

  setVoiceState(state) {
    if (!this.hasVoiceButtonTarget) return;

    const button = this.voiceButtonTarget;
    const icons = { idle: 'microphone', transcribing: 'spinner fa-spin' };
    button.innerHTML = state === 'recording'
      ? `<span class="voice-bars">${'<span class="voice-bar"></span>'.repeat(5)}</span>`
      : `<i class="fa fa-${icons[state]}"></i>`;
    button.disabled = state === 'transcribing';
    button.classList.toggle('btn-primary', state === 'recording');
    button.classList.toggle('btn-secondary', state !== 'recording');
    button.title = state === 'recording' ? 'Done' : 'Speak your question';
    [this.voiceCancelTarget, this.voiceConfirmTarget].forEach((control) => {
      control.classList.toggle('d-none', state !== 'recording');
    });
  }

  stopStream() {
    cancelAnimationFrame(this.voiceBarsFrame);
    this.audioContext?.close();
    this.audioContext = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  // --- Helpers ---

  scrollToBottom() {
    const container = document.getElementById('scroll-container');
    if (container) container.scrollTop = container.scrollHeight;
  }

  async fetchMentions(query) {
    const types = this.mentionableTypesValue || ['User'];
    const results = [];

    for (const type of types) {
      try {
        const url = `${this.mentionsUrlValue}?q=${encodeURIComponent(query)}&type=${encodeURIComponent(type)}`;
        const response = await fetch(url, {
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (response.ok) {
          results.push(...await response.json());
        }
      } catch (e) {
        console.error('Error fetching mentions:', e);
      }
    }

    return results;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
